import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { cloneDeep, omit } from 'lodash';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import Modal from 'components/Layout/Modal';
import Dropdown from 'components/form/Dropdown';
import Button from 'components/form/Button/Button';
import Loader from 'components/Layout/Loader';
import { limit } from 'utils/constants';
import { objectToFormData } from 'utils/helpers';
import {
  addEventParticipants,
  editEventParticipants,
  fetchAllProfiles,
  showSuccess,
  throwError,
} from 'store/slices';
import './ParticipantsForm.scss';
import FileUpload from 'components/form/FileUpload';
import Label from 'components/form/Label';

const validationSchema = Yup.object().shape({
  participants: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Name is required'),
      // email_id: Yup.string()
      //   .email('Invalid email')
      //   .required('Email is required'),
      join_as: Yup.string().required('Join as is required'),
    })
  ),
});

const initialValues = {
  participants: [
    {
      name: '',
      user_id: '',
      email_id: '',
      join_as: '',
    },
  ],
};

const DropdownInput = ({
  index,
  value,
  onChange,
  onSearch,
  onOptionClick,
  isLoading,
  userList,
  nameValue,
  onScroll,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  const handleClickOutside = useCallback(
    (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        e?.target?.id !== `user-creatable-${index}`
      ) {
        setIsOpen(false);
      }
    },
    [index]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className="custom-input-container position-relative">
      <input
        id={`user-creatable-${index}`}
        type="text"
        value={value}
        placeholder="Select or invite user"
        onChange={(e) => {
          onChange(e.target.value); // update Formik value
          onSearch(e); // trigger search logic
        }}
        onClick={() => setIsOpen(true)}
        className={`form-control ${value ? 'input-has-value' : ''}`}
        autoComplete="off"
      />
      <div className="icon-block">
        {isLoading ? (
          <Loader size="sm" />
        ) : (
          <i className="bi bi-chevron-down" />
        )}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="input-options box-shadow iferp-scroll"
          style={{ maxHeight: 200, overflowY: 'auto', zIndex: 999 }}
          onScroll={onScroll}
        >
          {userList.length === 0 ? (
            nameValue ? (
              <div
                className="ps-3 pe-2 pt-3 pb-3 bg-create cursor-pointer"
                onClick={() => {
                  onOptionClick(index, nameValue, true);
                  setIsOpen(false);
                }}
              >
                {`Invite ${nameValue}`}
              </div>
            ) : (
              <div className="pt-3 pb-3 text-center">No users found</div>
            )
          ) : (
            <>
              {userList.map((user, idx) => (
                <div
                  key={user.id || idx}
                  className="ps-3 pe-2 pt-2 pb-2 border-bottom cursor-pointer"
                  onClick={() => {
                    onOptionClick(index, user);
                    setIsOpen(false);
                  }}
                >
                  {`${user.firstName} ${user.lastName}`}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ParticipantsForm = ({ onHide, getParticipants, oldData }) => {
  const dispatch = useDispatch();
  const params = useParams();

  const [timer, setTimer] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [editFormData, setEditFormData] = useState(
    oldData || {
      join_as: '',
    }
  );
  const [userList, setUserList] = useState({
    list: [],
    name: '',
    total: 0,
    offset: 0,
    limit: limit,
    isLoading: true,
  });

  const fetchUserList = useCallback(
    async (params, isReset = false) => {
      try {
        const { name, ...rest } = omit(params, ['list', 'total', 'isLoading']);
        const modifiedParams = {
          ...rest,
          name_with_email: name,
          without_mask: true,
        };

        // console.log('✌️modifiedParams --->', modifiedParams);
        const payload = objectToFormData(modifiedParams);
        // console.log('✌️payload --->', payload);

        const response = await dispatch(fetchAllProfiles(payload));
        setUserList((prev) => ({
          ...prev,
          list: isReset
            ? response?.data?.users || []
            : [...prev.list, ...(response?.data?.users || [])],
          total: response?.data?.result_count || 0,
          isLoading: false,
        }));
      } catch (error) {
        console.error('Error fetching user list:', error);
        setUserList((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchUserList(userList, true);
  }, []); // eslint-disable-line

  useEffect(() => {
    return () => {
      clearTimeout(timer);
    };
  }, [timer]);

  const handleEdit = async () => {
    setBtnLoading(true);
    try {
      const payload = {
        id: editFormData.id,
        join_as: editFormData.join_as,
      };
      const response = await dispatch(
        editEventParticipants(objectToFormData(payload))
      );
      if (response?.status === 200) {
        getParticipants();
        onHide();
      }
    } catch (error) {
      console.error('Error editing participant:', error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleSave = async (values) => {
    setBtnLoading(true);
    try {
      const payload = {
        event_id: params?.eventId,
        participants_details: JSON.stringify(values.participants),
      };
      const response = await dispatch(
        addEventParticipants(objectToFormData(payload))
      );
      if (response?.status === 200) {
        getParticipants();
        onHide();
        dispatch(showSuccess(response.message));
      }
    } catch (error) {
      console.error('Error saving participants:', error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleScroll = useCallback(
    (e) => {
      const { scrollHeight, scrollTop, clientHeight } = e.target;
      const isBottom = scrollHeight - scrollTop <= clientHeight + 10;
      if (isBottom && userList.list.length < userList.total) {
        const newOffset = userList.offset + userList.limit;
        fetchUserList({ ...userList, offset: newOffset });
      }
    },
    [userList]
  );

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value;
      setUserList((prev) => ({ ...prev, name: value, isLoading: true }));

      clearTimeout(timer);
      const newTimer = setTimeout(() => {
        fetchUserList(
          {
            ...userList,
            offset: 0,
            name: value.toLowerCase(),
          },
          true
        );
      }, 800);
      setTimer(newTimer);
    },
    [timer, userList]
  );

  if (oldData) {
    return (
      <Modal
        onHide={onHide}
        titleBesideClose="Edit Participant"
        isTitleBorder
        isTitleVerticalCenter
        titleBesideCloseClassName={'text-16-300-20'}
      >
        <div className="mt-4 ps-3 pe-3 pb-3" id="participants-form-container">
          <div className="custom-input-container mb-3">
            <input
              type="text"
              value={editFormData?.name || editFormData?.email_id}
              readOnly
              className="form-control"
            />
          </div>
          <div className="mb-3">
            <Dropdown
              placeholder="Join As"
              value={editFormData.join_as}
              options={[
                { id: 'presenter', label: 'Presenter' },
                { id: 'listener', label: 'Listener' },
              ]}
              onChange={(e) => {
                setEditFormData((prev) => ({
                  ...prev,
                  join_as: e.target.value,
                }));
              }}
            />
          </div>
          <div className="d-flex mt-4">
            <Button
              disabled={btnLoading}
              btnStyle="primary-dark"
              className="ps-5 pe-5 h-35"
              onClick={handleEdit}
              btnLoading={btnLoading}
              text="SAVE"
            />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onHide={onHide}
      titleBesideClose="Add Participants"
      isTitleBorder
      isTitleVerticalCenter
      titleBesideCloseClassName={'text-16-300-20'}
    >
      <div className="cp-20">
        <Formik
          initialValues={initialValues}
          onSubmit={handleSave}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({
            values,
            errors,
            setFieldValue,
            dirty,
            resetForm,
            handleSubmit,
          }) => {
            const handleOptionClick = (index, item, isCreate = false) => {
              if (isCreate) {
                const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item);
                if (!isValidEmail) {
                  dispatch(
                    throwError({ message: 'Please enter a valid email' })
                  );
                  return;
                }

                setFieldValue(`participants.${index}`, {
                  name: item,
                  user_id: '',
                  email_id: item,
                  join_as: '',
                });
              } else {
                setFieldValue(`participants.${index}`, {
                  name: `${item.firstName} ${item.lastName}`,
                  user_id: item.id,
                  email_id: item.email,
                  join_as: '',
                });
              }
              setUserList((prev) => ({ ...prev, name: '' }));
            };

            return (
              <form>
                <FieldArray name="participants">
                  {({ push, remove }) => (
                    <>
                      {values?.participants.map((service, index) => {
                        return (
                          <div
                            key={index}
                            className="row g-3 mb-3 align-items-center"
                            id="participants-form-container"
                          >
                            <div className="col-lg-6 col-md-6 col-12 mb-2">
                              <Label label={'User'} />
                              <DropdownInput
                                index={index}
                                value={service.name}
                                onChange={(value) =>
                                  setFieldValue(
                                    `participants.${index}.name`,
                                    value
                                  )
                                }
                                onSearch={handleSearch}
                                onOptionClick={(idx, item, isCreate) =>
                                  handleOptionClick(idx, item, isCreate)
                                }
                                isLoading={userList.isLoading}
                                userList={userList.list}
                                nameValue={userList.name}
                                onScroll={handleScroll}
                              />
                              {errors.participants?.[index]?.name && (
                                <div className="text-danger small mt-1">
                                  {errors.participants[index].name}
                                </div>
                              )}
                            </div>

                            <div className="col-lg-5 col-md-5 col-12 mb-2">
                              <Dropdown
                                label={'Join As'}
                                name={`participants.${index}.join_as`}
                                placeholder="Join As"
                                value={service.join_as}
                                options={[
                                  { id: 'presenter', label: 'Presenter' },
                                  { id: 'listener', label: 'Listener' },
                                ]}
                                onChange={(e) =>
                                  setFieldValue(
                                    `participants.${index}.join_as`,
                                    e.target.value
                                  )
                                }
                              />
                              {errors.participants?.[index]?.join_as && (
                                <div className="text-danger small mt-1">
                                  {errors.participants[index].join_as}
                                </div>
                              )}
                            </div>

                            <div className="col-lg-1 col-md-1 col-12 mb-2 d-flex align-items-center justify-content-center gap-2 pb-2">
                              {values.participants.length > 1 &&
                                index !== values.participants.length - 1 && (
                                  <Button
                                    type="button"
                                    icon={
                                      <i className="bi bi-trash fs-3 fw-bold" />
                                    }
                                    className="h-35"
                                    onClick={() => remove(index)}
                                  />
                                )}

                              {index === values.participants.length - 1 && (
                                <Button
                                  type="button"
                                  icon={
                                    <i className="bi bi-plus-circle fs-3 fw-bold" />
                                  }
                                  className="h-35"
                                  onClick={() =>
                                    push({
                                      name: '',
                                      user_id: '',
                                      email_id: '',
                                      join_as: '',
                                    })
                                  }
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}

                      <div className="d-flex mt-4 gap-3">
                        <Button
                          btnStyle="primary-dark"
                          text="Save"
                          className="h-37 text-14-500 text-nowrap cps-40 cpe-40"
                          onClick={handleSubmit}
                          btnLoading={btnLoading}
                          disabled={btnLoading}
                        />

                        <Button
                          btnStyle="light-outline"
                          text={`${dirty ? 'Reset' : 'Cancel'}`}
                          className="h-37 text-14-500 text-nowrap cps-40 cpe-40"
                          type="button"
                          disabled={btnLoading}
                          onClick={() => {
                            if (dirty) {
                              resetForm();
                            } else {
                              onHide();
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                </FieldArray>
              </form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};

export default ParticipantsForm;

// import React, { useEffect, useRef, useState } from "react";
// import { useParams } from "react-router-dom";
// import { useDispatch } from "react-redux";
// import { cloneDeep, omit } from "lodash";
// import Modal from "components/Layout/Modal";
// import Dropdown from "components/form/Dropdown";
// import Button from "components/form/Button/Button";
// import Loader from "components/Layout/Loader";
// import { limit } from "utils/constants";
// import { objectToFormData } from "utils/helpers";
// import {
//   addEventParticipants,
//   editEventParticipants,
//   fetchAllProfiles,
//   throwError,
// } from "store/slices";
// import "./ParticipantsForm.scss";

// const ParticipantsForm = ({ onHide, getParticipants, oldData }) => {
//   const dispatch = useDispatch();
//   const myRef = useRef();
//   const params = useParams();
//   const [timer, setTimer] = useState("");
//   const [btnLoading, setBtnLoading] = useState(false);
//   const [isOpen, setisOpen] = useState(false);
//   const [selectedList, setSelectedList] = useState([]);
//   const [data, setData] = useState({
//     name: "",
//     user_id: "",
//     email_id: "",
//     join_as: oldData?.join_as || "",
//   });
//   const [userList, setUserList] = useState({
//     list: [],
//     name: "",
//     total: 0,
//     offset: 0,
//     limit: limit,
//     isLoading: true,
//   });
//   const handleEdit = async () => {
//     setBtnLoading(true);
//     const payload = {
//       id: oldData.id,
//       join_as: data.join_as,
//     };
//     const response = await dispatch(
//       editEventParticipants(objectToFormData(payload))
//     );
//     if (response?.status === 200) {
//       getParticipants();
//       onHide();
//     }
//     setBtnLoading(false);
//   };
//   const handleSave = async () => {
//     setBtnLoading(true);
//     const payload = {
//       event_id: params?.eventId,
//       participants_details: JSON.stringify(selectedList),
//     };
//     const response = await dispatch(
//       addEventParticipants(objectToFormData(payload))
//     );
//     if (response?.status === 200) {
//       getParticipants();
//       onHide();
//     }
//     setBtnLoading(false);
//   };
//   const handleAdd = () => {
//     let alreadyExist = selectedList.some((o) => o.email_id === data.email_id);
//     if (alreadyExist) {
//       dispatch(
//         throwError({ message: "This user email already exist in list." })
//       );
//     } else {
//       setSelectedList((prev) => {
//         return [...prev, data];
//       });
//       setData({
//         name: "",
//         user_id: "",
//         email_id: "",
//         join_as: "",
//       });
//       setUserList((prev) => {
//         return { ...prev, name: "" };
//       });
//       fetchUserList({ ...userList, name: "" }, true);
//     }
//   };
//   const handleOptionClick = (e, isCreate = false) => {
//     let object = {
//       name: "",
//       user_id: "",
//       email_id: "",
//     };
//     if (isCreate) {
//       const isValidEmail = /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/.test(e);
//       if (isValidEmail) {
//         object.email_id = e;
//       } else {
//         dispatch(
//           throwError({
//             message: "Please enter valid email",
//           })
//         );
//       }
//     } else {
//       object = {
//         name: `${e?.firstName} ${e?.lastName}`,
//         user_id: e.id,
//         email_id: e.email,
//       };
//     }
//     setData((prev) => {
//       return { ...prev, ...object };
//     });
//     setUserList((prev) => {
//       return { ...prev, name: "" };
//     });
//     setisOpen(false);
//   };
//   const handleScroll = (e) => {
//     const bottom =
//       e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
//     if (bottom) {
//       let oldData = cloneDeep({
//         ...userList,
//         offset: userList.offset + 20,
//         isLoading: true,
//       });
//       setUserList(oldData);
//       fetchUserList(oldData);
//     }
//   };
//   const handleSearch = (e) => {
//     let value = e.target.value;
//     setUserList((prev) => {
//       return { ...prev, name: value, isLoading: true };
//     });
//     let time = timer;
//     clearTimeout(time);
//     time = setTimeout(() => {
//       let oldData = cloneDeep({
//         ...userList,
//         offset: 0,
//         name: value.toLowerCase(),
//       });
//       setUserList(oldData);
//       fetchUserList(oldData, true);
//     }, 800);
//     setTimer(time);
//   };
//   const fetchUserList = async (obj, isReset) => {
//     let payload = objectToFormData(
//       omit({ ...obj, email: obj?.name || "" }, [
//         "name",
//         "list",
//         "total",
//         "offset",
//         "limit",
//         "isLoading",
//       ])
//     );
//     const response = await dispatch(fetchAllProfiles(payload));
//     setUserList((prev) => {
//       let resData = response?.data?.users || [];
//       let listData = isReset ? resData : [...prev.list, ...resData];
//       return {
//         ...prev,
//         list: listData,
//         total: response?.data?.result_count || 0,
//         isLoading: false,
//       };
//     });
//   };
//   const handleClickOutside = (e) => {
//     if (
//       myRef.current &&
//       !myRef.current.contains(e.target) &&
//       e?.target?.id !== "user-creatable"
//     ) {
//       setisOpen(false);
//     }
//   };
//   useEffect(() => {
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   });
//   useEffect(() => {
//     fetchUserList(userList, true);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);
//   const { list, isLoading, total, name } = userList;
//   return (
//     <Modal title={`${oldData ? "Edit" : "Add"} Participants`} onHide={onHide}>
//       {oldData ? (
//         <div className="mt-4 ps-2 pe-2" id="participants-form-container">
//           <div className="custom-input-container mb-3">
//             <input
//               id="user-creatable"
//               type="text"
//               value={oldData?.name || oldData?.email_id}
//               onChange={() => {}}
//               readOnly
//             />
//           </div>
//           <div className="mb-3">
//             <Dropdown
//               placeholder="Join As"
//               value={data.join_as}
//               options={[
//                 {
//                   id: "presenter",
//                   label: "Presenter",
//                 },
//                 {
//                   id: "listener",
//                   label: "Listener",
//                 },
//               ]}
//               onChange={(e) => {
//                 setData((prev) => {
//                   return { ...prev, join_as: e.target.value };
//                 });
//               }}
//             />
//           </div>
//           <div className="d-flex justify-content-center mt-4">
//             <Button
//               disabled={oldData.join_as === data.join_as}
//               btnStyle="primary-dark"
//               className="ps-5 pe-5"
//               onClick={handleEdit}
//               btnLoading={btnLoading}
//               text="SAVE"
//             />
//           </div>
//         </div>
//       ) : (
//         <>
//           <div className="mt-4 ps-2 pe-2 row" id="participants-form-container">
//             <div className="col-md-6 mb-3">
//               <div className="custom-input-container">
//                 <input
//                   id="user-creatable"
//                   type="text"
//                   value={name}
//                   placeholder={
//                     data.name || data.email_id || "Select or invite user"
//                   }
//                   onChange={handleSearch}
//                   className={
//                     name || data.email_id || data.name ? "input-has-value" : ""
//                   }
//                   onClick={() => {
//                     setisOpen(true);
//                   }}
//                 />

//                 <div className="icon-block">
//                   {isLoading ? (
//                     <Loader size="sm" />
//                   ) : (
//                     <i className="bi bi-chevron-down" />
//                   )}
//                 </div>
//                 {isOpen && (
//                   <div
//                     className="input-options box-shadow iferp-scroll"
//                     ref={myRef}
//                     onScroll={(e) => {
//                       if (list.length < total) {
//                         handleScroll(e);
//                       }
//                     }}
//                   >
//                     {list.length === 0 ? (
//                       name ? (
//                         <div
//                           className="ps-3 pe-2 pt-3 pb-3 bg-create"
//                           onClick={() => {
//                             handleOptionClick(name, true);
//                           }}
//                         >
//                           {`Create ${name}`}
//                         </div>
//                       ) : (
//                         <div className="pt-3 pb-3 center-flex">
//                           No Data Found
//                         </div>
//                       )
//                     ) : (
//                       list?.map((elm, index) => {
//                         return (
//                           <div
//                             key={index}
//                             className="ps-3 pe-2 pt-2 pb-2 border-bottom"
//                             onClick={() => {
//                               handleOptionClick(elm);
//                             }}
//                           >
//                             {`${elm?.firstName} ${elm?.lastName}`}
//                           </div>
//                         );
//                       })
//                     )}
//                   </div>
//                 )}
//               </div>
//             </div>
//             <div className="col-md-4 mb-2">
//               <Dropdown
//                 placeholder="Join As"
//                 value={data.join_as}
//                 options={[
//                   {
//                     id: "presenter",
//                     label: "Presenter",
//                   },
//                   {
//                     id: "listener",
//                     label: "Listener",
//                   },
//                 ]}
//                 onChange={(e) => {
//                   setData((prev) => {
//                     return { ...prev, join_as: e.target.value };
//                   });
//                 }}
//               />
//             </div>
//             <div className="col-md-2 mb-2">
//               <div className="d-flex justify-content-end">
//                 <Button
//                   disabled={!data.email_id || !data.join_as}
//                   rightIcon={<i className="bi bi-plus-circle ms-2" />}
//                   btnStyle="primary-dark"
//                   className="ps-3 pe-3 h-45"
//                   onClick={handleAdd}
//                   text="Add"
//                 />
//               </div>
//             </div>
//           </div>
//           <div className="d-flex gap-2 flex-wrap ps-2 pe-2">
//             {selectedList.map((elm, index) => {
//               return (
//                 <span
//                   key={index}
//                   className="d-flex align-items-center gap-1 border p-1 ps-2 pe-2"
//                 >
//                   <span className="text-14-500">
//                     {elm.name || elm.email_id}
//                   </span>
//                   <span className="ms-2">
//                     <i
//                       className="bi bi-trash-fill text-danger pointer"
//                       onClick={() => {
//                         setSelectedList((prev) => {
//                           return prev.filter((_, pIndex) => pIndex !== index);
//                         });
//                       }}
//                     />
//                   </span>
//                 </span>
//               );
//             })}
//           </div>

//           <div className="d-flex justify-content-center mt-4">
//             <Button
//               disabled={selectedList.length === 0}
//               btnStyle="primary-dark"
//               className="ps-5 pe-5"
//               onClick={handleSave}
//               btnLoading={btnLoading}
//               text="SAVE"
//             />
//           </div>
//         </>
//       )}
//     </Modal>
//   );
// };

// export default ParticipantsForm;
